// PayrollProcess

import { useCallback, useEffect, useState } from "react";
import { Reactable } from "../../components/ui/reactable";
import { EMPLOYEE_DEPARTMENT, EMPLOYEE_TYPE, PAYROLL_PERIOD, PAYROLL_RUN_GET, PAYROLL_SHORT_DUTY_REVIEW, CUSTOM_CONFIGURATION } from "../../constants/api";
import { useNavigate } from "react-router-dom";
import { Eye, FileText } from "lucide-react";
import { Button } from "../../components/ui/button";
import { cn } from "../../utils/tailwindUtils";
import { ContainerCard, ModuleTitleCard } from "../../components/ui/cards";
import { checkUserPermissionFunc, truncateText } from "../../components/common/CommonFunctions";
import moment from "moment";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import useApiMethods from "../../lib/useApiMethods";
import { DialogModal } from "../../components/ui/dialogModal";
import { DatePicker, Select } from "antd";
import { Input } from "../../components/ui/input";
import { antPickerClass, antSelectClass } from "../../components/ui/fieldStyles";
import ToastMsg from "../../components/common/ToastMsg";
import PayrollProfileLockBanner from "./PayrollProfileLockBanner";

const { Option } = Select;

const MIN_PAYROLL_PERIOD_DAYS = 20;

dayjs.extend(customParseFormat);

const PAYROLL_MONTH_FORMAT = "MMMM YYYY";

export const disabledFutureDate = (current) => {
    if (!current) return false;
    return current.endOf("month").valueOf() > dayjs().endOf("month").valueOf();
};

const payrollFieldClass = "w-full";
const selectClass = antSelectClass("w-full");
const pickerClass = antPickerClass("w-full");

const PayrollFormField = ({ label, required, children, className }) => (
    <div className={cn("space-y-1", className)}>
        <label className="text-xs font-medium text-gray-700 dark:text-gray-300">
            {label}
            {required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
        {children}
    </div>
);

const PayrollProcess = () => {
    const { fetch, create } = useApiMethods();
    const { add: addPermission, edit: editPermission, active: isActive } = checkUserPermissionFunc("payroll", "Payroll", "SubMenu");

    const [payrollProcessData, setPayrollProcessData] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [serverTrigger] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [loading, setLoading] = useState(false);
    const [payrollConfig, setPayrollConfig] = useState({
        enforce_one_payslip_per_month: true,
        allow_supplement_payroll: true,
    });

    const [dropdownList, setDropdownList] = useState({
        employeeTypeList: [],
        departmentList: [],
        payrollPeriodList: [],
    });

    const navigate = useNavigate();

    // Form state
    const [formData, setFormData] = useState({
        payroll_date: moment().format("DD-MM-YYYY HH:mm:ss"),
        payroll_month_year: "",
        from_date: "",
        to_date: "",
        employee_type: [],
        department: [],
        payroll_period: "",
        total_days: "",
        run_type: "regular",
        supplement_reason: "",
    });

    // Reset form & state
    const resetForm = () => {
        setFormData({
            payroll_date: moment().format("DD-MM-YYYY HH:mm:ss"),
            payroll_month_year: "",
            from_date: "",
            to_date: "",
            employee_type: [],
            department: [],
            payroll_period: "",
            total_days: "",
            run_type: "regular",
            supplement_reason: "",
        });
        setOpenDialog(false);
    };

    // Fetch dropdown data
    const fetchDropdownData = useCallback(async () => {
        try {
            const [employeeTypeRes, departmentRes, payrollPeriodRes] = await Promise.all([
                fetch(EMPLOYEE_TYPE, { drop_down: 'True' }),
                fetch(EMPLOYEE_DEPARTMENT, { drop_down: 'True' }),
                fetch(PAYROLL_PERIOD, { drop_down: 'True' })
            ]);

            setDropdownList({
                employeeTypeList: employeeTypeRes?.status === "success" ? employeeTypeRes.data : [],
                departmentList: departmentRes?.status === "success" ? departmentRes.data : [],
                payrollPeriodList: payrollPeriodRes?.status === "success" ? payrollPeriodRes.data : [],
            });
        } catch (error) {
            console.error("Error fetching dropdown data:", error);
            ToastMsg('error', 'Failed to load dropdown data');
        }
    }, []);

    const fetchPayrollConfig = useCallback(async () => {
        try {
            const res = await fetch(CUSTOM_CONFIGURATION);
            if (res?.status === "success" && res?.data) {
                setPayrollConfig({
                    enforce_one_payslip_per_month: res.data.enforce_one_payslip_per_month !== false,
                    allow_supplement_payroll: res.data.allow_supplement_payroll !== false,
                });
            }
        } catch (error) {
            console.error("Error fetching payroll config:", error);
        }
    }, []);

    useEffect(() => {
        fetchDropdownData();
        fetchPayrollConfig();
    }, [fetchDropdownData, fetchPayrollConfig]);

    // Capitalize function
    const capitalize = (str) => {
        if (!str) return "-";
        return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
    };

    // Status chip styling function
    const getStatusChipStyle = (status) => {
        const statusLower = status?.toLowerCase();

        switch (statusLower) {
            case 'pending':
                return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300 border border-yellow-200 dark:border-yellow-700";
            case 'completed':
                return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300 border border-green-200 dark:border-green-700";
            case 'processing':
                return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300 border border-blue-200 dark:border-blue-700";
            case 'failed':
                return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300 border border-red-200 dark:border-red-700";
            case 'draft':
                return "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600";
            default:
                return "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600";
        }
    };

    // Calculate from and to dates based on payroll period (pure — no setState).
    const computePayrollDates = (payrollPeriodId, payrollMonthYear) => {
        if (!payrollPeriodId || !payrollMonthYear) {
            return { from_date: "", to_date: "", total_days: "" };
        }

        const selectedPeriod = dropdownList.payrollPeriodList.find((item) => item.id === payrollPeriodId);
        if (!selectedPeriod) {
            return { from_date: "", to_date: "", total_days: "" };
        }

        const { start_day, end_day, month: selectedMonths, period } = selectedPeriod;
        const parsedDate = moment(payrollMonthYear, PAYROLL_MONTH_FORMAT, true);

        if (!parsedDate.isValid()) {
            return { from_date: "", to_date: "", total_days: "" };
        }

        const currentMonth = parsedDate.month() + 1;
        const currentYear = parsedDate.year();

        let newFromDate = "";
        let newToDate = "";
        let newTotalDays = 0;

        if (period === "month_wise") {
            const daysInCurrentMonth = moment(`${currentYear}-${currentMonth}`, 'YYYY-MM').daysInMonth();
            const startDay = Math.min(start_day, daysInCurrentMonth);
            const endDay = Math.min(end_day, daysInCurrentMonth);

            newFromDate = moment(`${currentYear}-${currentMonth}-${startDay}`, 'YYYY-MM-DD').format('DD-MM-YYYY');
            newToDate = moment(`${currentYear}-${currentMonth}-${endDay}`, 'YYYY-MM-DD').format('DD-MM-YYYY');
            newTotalDays = endDay - startDay + 1;
        } else if (period === "customize") {
            const daysInSelectedMonth = moment(`${currentYear}-${currentMonth}`, 'YYYY-MM').daysInMonth();
            const previousMonth = parsedDate.clone().subtract(1, 'month');
            const prevMonth = previousMonth.month() + 1;
            const prevYear = previousMonth.year();
            const daysInPrevMonth = previousMonth.daysInMonth();

            if (selectedMonths.includes(currentMonth) || selectedMonths.includes(0)) {
                let startDate;
                let endDate;
                if (start_day > end_day) {
                    const sd = Math.min(start_day, daysInPrevMonth);
                    const ed = Math.min(end_day, daysInSelectedMonth);
                    startDate = moment(`${prevYear}-${prevMonth}-${sd}`, 'YYYY-MM-DD');
                    endDate = moment(`${currentYear}-${currentMonth}-${ed}`, 'YYYY-MM-DD');
                } else {
                    const sd = Math.min(start_day, daysInSelectedMonth);
                    const ed = Math.min(end_day, daysInSelectedMonth);
                    startDate = moment(`${currentYear}-${currentMonth}-${sd}`, 'YYYY-MM-DD');
                    endDate = moment(`${currentYear}-${currentMonth}-${ed}`, 'YYYY-MM-DD');
                }
                newFromDate = startDate.format('DD-MM-YYYY');
                newToDate = endDate.format('DD-MM-YYYY');
                newTotalDays = endDate.diff(startDate, 'days') + 1;
            } else {
                ToastMsg("error", 'To run the payroll, please ensure that the payroll period has been configured');
                return { from_date: "", to_date: "", total_days: "" };
            }
        }

        return {
            from_date: newFromDate,
            to_date: newToDate,
            total_days: newTotalDays,
        };
    };

    // Handle form field changes
    const handleChange = (name, value) => {
        switch (name) {
            case "department":
            case "employee_type":
                setFormData(prev => ({
                    ...prev,
                    [name]: value.includes('all') ? ["all"] : value
                }));
                break;
            case "payroll_month_year":
                setFormData(prev => {
                    const monthYear = value || "";
                    const dates = computePayrollDates(prev.payroll_period, monthYear);
                    return { ...prev, payroll_month_year: monthYear, ...dates };
                });
                break;
            case "payroll_period":
                setFormData(prev => {
                    const dates = computePayrollDates(value, prev.payroll_month_year);
                    return { ...prev, payroll_period: value, ...dates };
                });
                break;
            default:
                setFormData(prev => ({ ...prev, [name]: value }));
                break;
        }
    };

    // Prepare payload for API
    const preparePayload = () => {
        const payrollDate = moment().format("YYYY-MM-DD HH:mm:ss");
        const payrollMonth = formData.payroll_month_year
            ? moment(formData.payroll_month_year, PAYROLL_MONTH_FORMAT, true).month() + 1
            : null;
        const payrollYear = formData.payroll_month_year
            ? moment(formData.payroll_month_year, PAYROLL_MONTH_FORMAT, true).year()
            : null;

        // Handle "all" selection for departments and employee types
        let departments = formData.department;
        let employeeTypes = formData.employee_type;

        // If "all" is selected, get all IDs from the dropdown lists
        if (departments.includes("all")) {
            departments = dropdownList.departmentList.map(dept => dept.id);
        }

        if (employeeTypes.includes("all")) {
            employeeTypes = dropdownList.employeeTypeList.map(type => type.id);
        }

        // Convert date formats from DD-MM-YYYY to YYYY-MM-DD
        const fromDate = formData.from_date ? moment(formData.from_date, "DD-MM-YYYY").format("YYYY-MM-DD") : null;
        const toDate = formData.to_date ? moment(formData.to_date, "DD-MM-YYYY").format("YYYY-MM-DD") : null;

        return {
            payroll_date: payrollDate,
            payroll_month: payrollMonth,
            payroll_year: payrollYear,
            department: departments,
            employee_type: employeeTypes,
            payroll_period: parseInt(formData.payroll_period) || null,
            from_date: fromDate,
            to_date: toDate,
            total_days: parseInt(formData.total_days) || 0,
            run_type: formData.run_type || "regular",
            supplement_reason: formData.run_type === "supplement"
                ? (formData.supplement_reason || "").trim()
                : "",
        };
    };

    // Save payroll process
    const handleSave = async () => {
        // Validate required fields
        if (!formData.payroll_month_year) {
            ToastMsg("error", "Please select Payroll Month & Year");
            return;
        }

        if (!formData.payroll_period) {
            ToastMsg("error", "Please select Payroll Period");
            return;
        }

        if (!formData.from_date || !formData.to_date) {
            ToastMsg("error", "Payroll period dates are not calculated. Please check your selections.");
            return;
        }

        if ((parseInt(formData.total_days, 10) || 0) < MIN_PAYROLL_PERIOD_DAYS) {
            ToastMsg(
                "error",
                `Payroll period must span at least ${MIN_PAYROLL_PERIOD_DAYS} days (selected span is ${formData.total_days} days).`
            );
            return;
        }

        if (formData.run_type === "supplement" && !(formData.supplement_reason || "").trim()) {
            ToastMsg("error", "Please enter a reason for supplement payroll (e.g. increment, bonus)");
            return;
        }

        if (formData.run_type === "supplement" && !payrollConfig.allow_supplement_payroll) {
            ToastMsg("error", "Supplement payroll is disabled in Settings → Configuration → Payroll");
            return;
        }

        const payload = preparePayload();
        if (!Array.isArray(payload.department) || payload.department.length === 0) {
            ToastMsg("error", "Please select Department");
            return;
        }
        if (!Array.isArray(payload.employee_type) || payload.employee_type.length === 0) {
            ToastMsg("error", "Please select Employee Type");
            return;
        }

        setLoading(true);
        try {
            const periodCheck = await create(PAYROLL_RUN_GET, { ...payload, validate_only: true });
            if (periodCheck?.status !== "success") {
                ToastMsg("error", periodCheck?.message || "Payroll has already been processed for this period");
                return;
            }

            // Only open the Short-Duty Review screen when the company has short-duty
            // deduction enabled (Settings) AND there are missing-punch days to review.
            // Otherwise the review adds nothing, so run payroll directly.
            const review = await fetch(PAYROLL_SHORT_DUTY_REVIEW, {
                from_date: payload.from_date,
                to_date: payload.to_date,
                department: payload.department,
                employee_type: payload.employee_type,
            });
            if (review?.status && review.status !== "success") {
                ToastMsg("error", review?.message || "Could not check short-duty days");
                return;
            }
            const enabled = !!review?.data?.config?.enabled;
            const count = review?.data?.count || 0;

            if (enabled && count > 0) {
                // Review gate — that screen finalises deductions then creates the payroll.
                resetForm();
                navigate('/payroll/shortdutyreview', { state: { payload, review: review.data } });
                return;
            }

            // Disabled or nothing to review → create the payroll now.
            const response = await create(PAYROLL_RUN_GET, payload);
            if (response?.status === "success") {
                ToastMsg("success", "Payroll process started successfully");
                resetForm();
                navigate('/payroll/runpayrollprocess', { state: { PaySlipId: response.data } });
            } else {
                ToastMsg("error", response?.message || "Failed to start payroll process");
                if (Array.isArray(response?.blocked_employees) && response.blocked_employees.length) {
                    console.warn("Blocked employees:", response.blocked_employees);
                }
            }
        } catch (error) {
            console.error("Error starting payroll process:", error);
            ToastMsg("error", error.message || "Failed to start payroll process");
        } finally {
            setLoading(false);
        }
    };

    const handleRunPayroll = () => {
        setOpenDialog(true);
    };

    const PayrollProcessColumns = [
        {
            accessor: "payroll_date",
            Header: "Payroll Date",
            sortable: true,
            filterable: true,
            filterType: 'date',
            Cell: ({ value }) => {
                if (!value) return "-";
                return moment(value).format("DD-MM-YYYY");
            }
        },
        {
            accessor: "payroll_month",
            Header: "Payroll Month",
            sortable: true,
            filterable: true,
            filterType: 'month',
            Cell: ({ value, row }) => {
                if (!value) return "-";
                const year = row?.payroll_year;
                const payrollMonth = year
                    ? moment(`${year}-${String(value).padStart(2, "0")}-01`).format("MMMM YYYY")
                    : moment().month(value - 1).format("MMMM");
                return (
                    <p className="text-gray-900 dark:text-white">
                        {`Payroll For ${payrollMonth}`}
                    </p>
                );
            }
        },
        {
            accessor: "employee_type",
            Header: "Employee Type",
            Cell: ({ row }) => {
                const employeeTypes = row?.employee_type;
                let employeeTypeText = "-";

                if (Array.isArray(employeeTypes) && employeeTypes.length > 0) {
                    employeeTypeText = employeeTypes
                        .map(type => type?.employee_type_name || type?.name || "")
                        .filter(Boolean)
                        .join(", ");
                } else if (typeof employeeTypes === 'string') {
                    employeeTypeText = employeeTypes;
                } else if (employeeTypes && typeof employeeTypes === 'object') {
                    employeeTypeText = employeeTypes.employee_type_name || employeeTypes.name || "-";
                }

                return (
                    <span className="text-gray-700 dark:text-gray-300">
                        {truncateText(employeeTypeText, 20, 'balance', 'No Employee Type')}
                    </span>
                );
            },
        },
        {
            accessor: "total_days",
            Header: "Days in period",
            Cell: ({ value }) => value ?? "-",
        },
        {
            accessor: "run_type",
            Header: "Run Type",
            sortable: true,
            filterable: true,
            Cell: ({ value, row }) => {
                const isSupplement = (value || "regular") === "supplement";
                return (
                    <div className="flex flex-col gap-0.5">
                        <span className={cn(
                            "inline-flex w-fit items-center px-2 py-0.5 rounded-full text-xs font-medium",
                            isSupplement
                                ? "bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-200"
                                : "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200"
                        )}>
                            {isSupplement ? "Supplement" : "Regular"}
                        </span>
                        {isSupplement && row?.supplement_reason && (
                            <span className="text-[11px] text-gray-500 dark:text-gray-400 truncate max-w-[140px]" title={row.supplement_reason}>
                                {row.supplement_reason}
                            </span>
                        )}
                    </div>
                );
            },
        },
        {
            accessor: "status",
            Header: "Status",
            sortable: true,
            filterable: true,
            Cell: ({ value }) => {
                if (!value) return "-";
                const statusText = capitalize(value);
                const chipStyle = getStatusChipStyle(value);

                return (
                    <span className={cn(
                        "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium",
                        chipStyle
                    )}>
                        {statusText}
                    </span>
                );
            },
        },
        ...(editPermission ? [
            {
                accessor: "action",
                Header: "Action",
                sortable: false,
                Cell: ({ row }) => {
                    const status = row?.status?.toLowerCase();
                    const isPending = status === 'pending';

                    const handleOpenEdit = () => {
                        navigate('/payroll/runpayrollprocess', {
                            state: {
                                fromEdit: true,
                                editDetails: row,
                                isActive: isActive
                            }
                        });
                    };

                    return (
                        <div className="flex items-center gap-2">
                            <Button
                                variant="ghost"
                                size="sm"
                                className={cn(
                                    "hover:bg-blue-50 dark:hover:bg-gray-700",
                                    isPending
                                        ? "text-sidebar-primary dark:text-blue-400 cursor-pointer"
                                        : "text-gray-400 dark:text-gray-600 cursor-auto"
                                )}
                                onClick={handleOpenEdit}
                            >
                                <Eye className="h-4 w-4" />
                            </Button>
                        </div>
                    );
                },
            }
        ] : [])
    ];

    return (
        <>
            {/* Loading Overlay for entire screen */}
            {/* {runPayrollLoading && (
                <div className="fixed inset-0 bg-white dark:bg-gray-900 z-50 flex flex-col items-center justify-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-sidebar-primary mb-6"></div>
                    <p className="text-xl text-gray-700 dark:text-gray-300 mb-2">Processing Payroll</p>
                    <p className="text-gray-500 dark:text-gray-400">Please wait while we prepare your payroll data...</p>
                    <div className="mt-6 w-64 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                        <div className="bg-sidebar-primary h-2 rounded-full animate-pulse"></div>
                    </div>
                </div>
            )} */}

            <ContainerCard className="dark:bg-gray-900">
                {/* Header */}
                <ModuleTitleCard
                    Icon={FileText}
                    title={'Payroll Process'}
                    subtitle="Manage and process employee payroll calculations"
                    totalRecords={totalRecords}
                    buttonEnable={!!addPermission}
                    setOpenDialog={handleRunPayroll}
                    buttonName={'Run Payroll'}
                />

                <PayrollProfileLockBanner className="mb-4" />

                <Reactable
                    columns={PayrollProcessColumns}
                    data={payrollProcessData}
                    setData={setPayrollProcessData}
                    setTotalCount={setTotalRecords}
                    url={PAYROLL_RUN_GET}
                    serverSide={true}
                    serverTrigger={serverTrigger}
                    rowSize={editPermission ? "sm" : null}
                    isDraftRequired={false}
                    isCSVRequired={true}
                    isPDFRequired={true}
                />

                {/* Run Payroll Modal */}
                <DialogModal
                    open={openDialog}
                    onClose={resetForm}
                    onSave={handleSave}
                    title={"Run Payroll"}
                    closeLabel="Cancel"
                    saveLabel={"Run Payroll"}
                    loading={loading}
                    width="920px"
                    className="overflow-hidden max-h-[92vh]"
                >
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-3">
                        <PayrollFormField label="Payroll Generation Date & Time" required>
                            <Input value={formData.payroll_date} disabled className={payrollFieldClass} />
                        </PayrollFormField>

                        <PayrollFormField label="Payroll Month & Year" required>
                            <DatePicker
                                allowClear
                                picker="month"
                                format={PAYROLL_MONTH_FORMAT}
                                value={
                                    formData.payroll_month_year
                                        ? dayjs(formData.payroll_month_year, PAYROLL_MONTH_FORMAT)
                                        : null
                                }
                                onChange={(date) => handleChange(
                                    "payroll_month_year",
                                    date && date.isValid() ? date.format(PAYROLL_MONTH_FORMAT) : ""
                                )}
                                disabledDate={disabledFutureDate}
                                placeholder="Select month and year"
                                className={pickerClass}
                            />
                        </PayrollFormField>

                        <PayrollFormField label="Select Payroll Period" required>
                            <Select
                                value={formData.payroll_period}
                                onChange={(value) => handleChange("payroll_period", value)}
                                placeholder="Select payroll period"
                                className={selectClass}
                            >
                                {dropdownList.payrollPeriodList.map((period) => (
                                    <Option key={period.id} value={period.id}>
                                        {period.payroll_period_name}
                                    </Option>
                                ))}
                            </Select>
                        </PayrollFormField>

                        <PayrollFormField label="Payroll From Date">
                            <DatePicker
                                value={formData.from_date ? moment(formData.from_date, "DD-MM-YYYY") : null}
                                format="DD-MM-YYYY"
                                className={pickerClass}
                                disabled
                            />
                        </PayrollFormField>

                        <PayrollFormField label="Payroll To Date">
                            <DatePicker
                                value={formData.to_date ? moment(formData.to_date, "DD-MM-YYYY") : null}
                                format="DD-MM-YYYY"
                                className={pickerClass}
                                disabled
                            />
                        </PayrollFormField>

                        <PayrollFormField label="Days in period">
                            <Input value={formData.total_days} disabled className={payrollFieldClass} />
                        </PayrollFormField>

                        <PayrollFormField label="Select Department" required className="sm:col-span-2 lg:col-span-1">
                            <Select
                                mode="multiple"
                                maxTagCount="responsive"
                                value={formData.department}
                                onChange={(value) => handleChange("department", value)}
                                placeholder="Select departments"
                                className={selectClass}
                            >
                                <Option key="all" value="all">All Departments</Option>
                                {dropdownList.departmentList.map((dept) => (
                                    <Option key={dept.id} value={dept.id}>
                                        {dept.department_name}
                                    </Option>
                                ))}
                            </Select>
                        </PayrollFormField>

                        <PayrollFormField label="Select Employee Type" required className="sm:col-span-2 lg:col-span-2">
                            <Select
                                mode="multiple"
                                maxTagCount="responsive"
                                value={formData.employee_type}
                                onChange={(value) => handleChange("employee_type", value)}
                                placeholder="Select employee types"
                                className={selectClass}
                            >
                                <Option key="all" value="all">All Employee Types</Option>
                                {dropdownList.employeeTypeList.map((type) => (
                                    <Option key={type.id} value={type.id}>
                                        {type.employee_type_name}
                                    </Option>
                                ))}
                            </Select>
                        </PayrollFormField>

                        {payrollConfig.enforce_one_payslip_per_month && (
                            <>
                                <PayrollFormField
                                    label="Payroll run type"
                                    className="sm:col-span-2 lg:col-span-3"
                                >
                                    <Select
                                        value={formData.run_type}
                                        onChange={(value) => handleChange("run_type", value)}
                                        className={selectClass}
                                    >
                                        <Option value="regular">Regular — first run for this month</Option>
                                        {payrollConfig.allow_supplement_payroll && (
                                            <Option value="supplement">Supplement — increment / bonus re-run</Option>
                                        )}
                                    </Select>
                                    <p className="text-[11px] leading-snug text-gray-500 dark:text-gray-400 pt-0.5">
                                        One regular payslip per employee per month. Use supplement only after the regular run.
                                    </p>
                                </PayrollFormField>

                                {formData.run_type === "supplement" && (
                                    <PayrollFormField
                                        label="Supplement reason"
                                        required
                                        className="sm:col-span-2 lg:col-span-3"
                                    >
                                        <Input
                                            value={formData.supplement_reason}
                                            onChange={(e) => handleChange("supplement_reason", e.target.value)}
                                            placeholder="e.g. Mid-month increment, Festival bonus"
                                            className={payrollFieldClass}
                                        />
                                    </PayrollFormField>
                                )}
                            </>
                        )}
                    </div>
                </DialogModal>
            </ContainerCard>
        </>
    );
};

export default PayrollProcess;